import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { FiCheck, FiAward, FiArrowLeft, FiZap, FiCreditCard, FiArrowDown } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { shopProducts, shopCategories } from '../data/shopData';
import SafeIcon from '../common/SafeIcon';
import MediaLogos from './MediaLogos';
import SchedulingForm from './SchedulingForm';
import StepProcess from './StepProcess';
import ProductCard from './ProductCard';

const ProductPage = () => {
  const { slug } = useParams();
  const [currency, setCurrency] = useState('MXN');
  const product = shopProducts.find(p => p.slug === slug);
  const whatsappNumber = "529844594738";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!product) return <div className="pt-40 text-center text-white">Producto no encontrado.</div>;

  const isMxn = currency === 'MXN';
  const displayPrice = isMxn ? product.priceMxn : product.priceUsd;
  const sales = product.salesContent;
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(`Hola! Quiero inscribirme al programa "${product.title}". ¿Me pueden ayudar con el proceso?`)}`;

  return (
    <div className="min-h-screen bg-black pt-32 pb-20">
      <Helmet>
        <title>{product.seoTitle || product.title} | Online Classes English</title>
        <meta name="description" content={product.description} />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4">
        <Link to="/shop" className="inline-flex items-center gap-2 text-gray-500 hover:text-white mb-8 transition-colors uppercase text-xs font-black tracking-widest">
          <FiArrowLeft /> Volver al Campus
        </Link>

        {/* Hero Product Info */}
        <div className="grid lg:grid-cols-2 gap-16 mb-24 items-start border-b border-zinc-900 pb-24">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }}>
            <div className="inline-flex items-center gap-2 bg-yellow-500/20 text-yellow-500 px-4 py-1 rounded-full mb-6 text-[10px] font-black uppercase tracking-widest border border-yellow-500/30">
              <SafeIcon icon={FiZap} className="animate-pulse" /> Contenido Actualizado 2026
            </div>
            <h1 className="text-5xl md:text-8xl font-black text-white mb-6 uppercase italic leading-[0.85] tracking-tighter">
              {product.title}
            </h1>
            <h2 className="text-2xl font-bold text-gray-300 mb-6 italic">{sales.headline}</h2>
            
            <div className="bg-zinc-900 border border-zinc-800 p-8 rounded-[3rem] shadow-2xl relative overflow-hidden mb-12">
              <div className="flex bg-black border border-zinc-700 rounded-full p-1 w-fit mb-8 relative z-10">
                <button onClick={() => setCurrency('MXN')} className={`px-6 py-2 rounded-full text-xs font-black transition-all ${currency === 'MXN' ? 'bg-yellow-500 text-black' : 'text-gray-500'}`}>MXN</button>
                <button onClick={() => setCurrency('USD')} className={`px-6 py-2 rounded-full text-xs font-black transition-all ${currency === 'USD' ? 'bg-yellow-500 text-black' : 'text-gray-500'}`}>USD</button>
              </div>
              <div className="flex items-baseline gap-2 mb-8 relative z-10">
                <span className="text-7xl font-black text-white">${displayPrice.toLocaleString()}</span>
                <span className="text-xl font-bold text-gray-500 uppercase">{currency}</span>
              </div>
              
              <div className="grid gap-4 relative z-10">
                {product.stripeUrl && (
                  <a href={product.stripeUrl} target="_blank" rel="noreferrer" className="w-full bg-indigo-600 text-white py-5 rounded-2xl font-black uppercase text-xl flex items-center justify-center gap-3 hover:bg-indigo-500 transition-all shadow-2xl border border-indigo-400/30">
                    <SafeIcon icon={FiCreditCard} /> Pagar con Tarjeta
                  </a>
                )}
                <a href={whatsappUrl} target="_blank" rel="noreferrer" className="w-full bg-green-600 text-white py-4 rounded-2xl font-black uppercase text-sm flex items-center justify-center gap-3 hover:bg-green-500 transition-all shadow-xl">
                  <SafeIcon icon={FaWhatsapp} /> Inscribirse por WhatsApp
                </a>
              </div>
            </div>
          </motion.div>

          <div className="space-y-8">
            <div className="bg-gradient-to-br from-zinc-900 to-black border border-zinc-800 p-8 rounded-[2.5rem]">
              <h3 className="text-xl font-black text-white mb-6 uppercase italic flex items-center gap-2">
                <SafeIcon icon={FiAward} className="text-yellow-500" /> Beneficios del Programa
              </h3>
              <div className="grid gap-4">
                {product.features.map((feat, i) => (
                  <div key={i} className="bg-black/50 p-4 rounded-2xl border border-zinc-800 flex items-center gap-4">
                    <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center text-green-500">
                      <SafeIcon icon={FiCheck} />
                    </div>
                    <span className="text-gray-300 font-bold uppercase text-[10px] tracking-widest">{feat}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ACTIVATION WORKFLOW SECTION */}
        <div className="py-24 border-b border-zinc-900">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-black text-white uppercase italic">Proceso de <span className="text-red-500">Activación</span></h2>
            <p className="text-gray-500 mt-4 max-w-2xl mx-auto">Sigue estos dos pasos para comenzar tus clases hoy mismo.</p>
          </div>
          <StepProcess />
          <div className="max-w-4xl mx-auto">
            <SchedulingForm />
          </div>
        </div>

        {/* FULL CATALOG - UNLIMITED SCROLL STYLE */}
        <div className="py-24">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-7xl font-black text-white uppercase italic tracking-tighter">Más <span className="text-yellow-500">Programas</span></h2>
            <p className="text-gray-500 font-bold uppercase tracking-widest mt-4">Explora todo el campus 2026</p>
          </div>

          {shopCategories.map((category) => {
            const products = shopProducts.filter(p => p.category === category.id && p.id !== product.id);
            if (products.length === 0) return null;
            return (
              <section key={category.id} className="mb-32">
                <div className="flex items-center gap-6 mb-12 border-b border-zinc-900 pb-6">
                  <div className="w-16 h-16 bg-zinc-900 rounded-2xl flex items-center justify-center text-yellow-500 border border-zinc-800">
                    <SafeIcon icon={category.icon} className="text-3xl" />
                  </div>
                  <div>
                    <h2 className="text-4xl font-black text-white uppercase italic tracking-tight">{category.name}</h2>
                    <p className="text-gray-500 font-bold uppercase text-xs tracking-widest">{products.length} Programas Recomendados</p>
                  </div>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {products.map(p => (
                    <ProductCard key={p.id} product={p} currency={currency} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>

        <div className="py-20">
          <MediaLogos />
        </div>
      </div>
    </div>
  );
};

export default ProductPage;